import { applyAccents } from './accent';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, getToken, onUnauthorized, setToken } from './api/client';
import type { Role, User } from './api/types';

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<User>;
  register: (email: string, name: string, password: string) => Promise<User>;
  logout: () => void;
  refresh: () => Promise<void>;
  /** UI convenience only; the API enforces every permission. */
  hasRole: (...roles: Role[]) => boolean;
  /** Whether the user's role grants a permission (UI only, like hasRole). */
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // The signed-in person's accent colours follow them to every page and device.
  useEffect(() => applyAccents(user?.accentLight, user?.accentDark), [user?.accentLight, user?.accentDark]);
  const [loading, setLoading] = useState(!!getToken());

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await api.me());
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    return onUnauthorized(() => setUser(null));
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      async login(email, password, remember = true) {
        const s = await api.login(email, password);
        setToken(s.token, remember);
        setUser(s.user);
        return s.user;
      },
      async register(email, name, password) {
        const s = await api.register(email, name, password);
        setToken(s.token);
        setUser(s.user);
        return s.user;
      },
      logout() {
        setToken(null);
        setUser(null);
      },
      refresh,
      hasRole: (...roles) => !!user && roles.includes(user.role),
      can: (permission) => !!user?.permissions?.includes(permission),
    }),
    [user, loading, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
